import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';
import { Error, LoaderPage, MaintenanceView } from '@utilities';
import Module from '../../layouts/Module';
import ModuleList from '../../components/ModuleList';
import SurveyNetworkError from '../../components/SurveyNetworkError';

import { GATES } from '@constants';
import { mapSurveyTypeToRoute, SURVEY_TABS } from './utilities/helpers';
import { MAINTENANCE_MESSAGE, readInstantSurveyTypes, SURVEY_TYPES } from '@api/instant_survey';

import { default as NumeratorLogo } from '../../components/Header/assets/data-wave-logo.svg?react';

const SURVEY_TYPE_GATES = {
  [SURVEY_TYPES.SEQUENTIAL_MONADIC]: GATES.SEQUENTIAL_MONADIC,
  [SURVEY_TYPES.VAN_WESTENDORP]: GATES.VAN_WESTENDORP,
  [SURVEY_TYPES.VIDEO]: GATES.QUESTION_TYPE_VIDEO,
};

const Surveys = () => {
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [surveyRoutes, setSurveyRoutes] = useState([]);

  const { client, isLoading: isStatsigLoading } = useStatsigClient();
  const isInstantSurveyMaintenance = client.checkGate(GATES.INSTANT_SURVEY_MAINTENANCE);
  const requestRef = useRef(null);

  useEffect(() => {
    if (!isStatsigLoading && !isInstantSurveyMaintenance) {
      getSurveyTypes();
    }

    return () => {
      requestRef.current?.abort();
    };
  }, [isStatsigLoading]);

  const getSurveyTypes = async () => {
    requestRef.current = new AbortController();

    try {
      const { results } = await readInstantSurveyTypes({ signal: requestRef.current.signal });
      const allowedSurveyTypes = results.filter((surveyType) => {
        const gate = SURVEY_TYPE_GATES[surveyType.id];
        return gate ? client.checkGate(gate) : true;
      });
      const surveyTypes = allowedSurveyTypes
        .filter(({ label }) => label === SURVEY_TABS[0].id)
        .map(mapSurveyTypeToRoute);
      const surveyLabs = allowedSurveyTypes
        .filter(({ label }) => label === SURVEY_TABS[1].id)
        .map(mapSurveyTypeToRoute);

      setSurveyRoutes([
        {
          ...SURVEY_TABS[0],
          routes: surveyTypes,
        },
        ...(surveyLabs.length > 0
          ? [
              {
                ...SURVEY_TABS[1],
                routes: surveyLabs,
              },
            ]
          : []),
      ]);
    } catch (error) {
      if (!requestRef.current.signal.aborted) {
        setError(error);
      }
    }
    setIsLoading(false);
  };

  if (isInstantSurveyMaintenance) {
    return <MaintenanceView heading={MAINTENANCE_MESSAGE} logo={<NumeratorLogo />} />;
  }
  if (isLoading || isStatsigLoading) return <LoaderPage />;
  if (error) {
    return error?.code === 'ERR_NETWORK' ? (
      <SurveyNetworkError />
    ) : (
      <Error status={error?.response?.status} />
    );
  }

  return (
    <Module route={{ routes: surveyRoutes }}>
      <Routes>
        {surveyRoutes.map((route) => (
          <Route
            element={<ModuleList routes={route.routes} type="survey" />}
            key={route.id}
            path={route.path}
          />
        ))}
        <Route element={<Navigate replace to={surveyRoutes[0].path} />} path="*" />
      </Routes>
    </Module>
  );
};

export default Surveys;
